/**
 * `/crm/organizations` — the companies deals are made with. Searchable by name,
 * paged, sortable by name or by when they were added.
 */
import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AddBusinessOutlinedIcon from '@mui/icons-material/AddBusinessOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import { CRM_PERMISSIONS, type OrganizationDto } from '@forsys/shared';
import { useAuth } from '../../auth/useAuth';
import { AsyncContent } from '../../components/AsyncContent';
import { EntityTable } from '../../components/EntityTable';
import { ErrorState } from '../../components/ErrorState';
import { FormDialog } from '../../components/FormDialog';
import { PageHeader } from '../../components/PageHeader';
import { SearchField } from '../../components/SearchField';
import { Timestamp } from '../../components/Timestamp';
import { useAsync } from '../../hooks/useAsync';
import { useCrudDialogs } from '../../hooks/useCrudDialogs';
import { useDebounced } from '../../hooks/useDebounced';
import { useListQuery } from '../../hooks/useListQuery';
import { useSubmit } from '../../hooks/useSubmit';
import { useOrganizationActions } from './actions';
import { createOrganization, listOrganizations } from './api';
import { BLANK_ORGANIZATION, createOrganizationRequest, OrganizationFields, type OrganizationForm } from './forms';

export default function OrganizationsPage() {
  const { t } = useTranslation('crm');
  const { can } = useAuth();
  const [term, setTerm] = useState('');
  const q = useDebounced(term.trim());

  const list = useListQuery({ defaultSort: 'name', resetKey: q });
  const organizations = useAsync(() => listOrganizations({ ...list.query, ...(q ? { q } : {}) }), [list.key, q]);
  const actions = useOrganizationActions({ onChanged: () => organizations.reload() });

  const crud = useCrudDialogs<OrganizationDto, OrganizationForm>({
    blank: BLANK_ORGANIZATION,
    formFor: () => BLANK_ORGANIZATION,
  });
  const create = useSubmit(async () => {
    const result = await createOrganization(createOrganizationRequest(crud.form));
    organizations.reload();
    return result;
  });

  return (
    <>
      <PageHeader
        title={t('organization.title')}
        description={t('organization.description')}
        actions={
          can(CRM_PERMISSIONS.WRITE) && (
            <Button variant="contained" startIcon={<AddBusinessOutlinedIcon />} onClick={() => crud.openCreate()}>
              {t('organization.new')}
            </Button>
          )
        }
      />

      <SearchField value={term} onChange={setTerm} placeholder={t('organization.searchPlaceholder')} sx={{ width: '100%', maxWidth: 420, mb: 2 }} />

      {actions.error && <ErrorState error={actions.error} />}

      <AsyncContent state={organizations}>
        {({ organizations: rows, page }) => (
          <EntityTable<OrganizationDto>
            rows={rows}
            rowKey={(organization) => organization.id}
            list={list}
            page={page}
            empty={{
              icon: <BusinessOutlinedIcon fontSize="inherit" />,
              title: q ? t('organization.noMatches') : t('organization.emptyTitle'),
              description: t('organization.emptyDescription'),
            }}
            columns={[
              {
                field: 'name',
                label: t('field.name'),
                sortable: true,
                render: (organization) => (
                  <Link
                    component={RouterLink}
                    to={`/crm/organizations/${organization.id}`}
                    underline="hover"
                    sx={{ fontWeight: 500 }}
                  >
                    {organization.name}
                  </Link>
                ),
              },
              { field: 'address', label: t('field.address'), render: (organization) => organization.address ?? '' },
              { field: 'owner', label: t('field.owner'), width: 170, render: (organization) => organization.owner.name },
              {
                field: 'createdAt',
                label: t('field.createdAt'),
                sortable: true,
                width: 150,
                render: (organization) => <Timestamp value={organization.createdAt} />,
              },
            ]}
            actions={actions.rowActions}
            actionsWidth={100}
          />
        )}
      </AsyncContent>
      {actions.dialogs}

      <FormDialog
        open={crud.isOpen}
        title={t('organization.createDialog.title')}
        submitLabel={t('organization.createDialog.submit')}
        pending={create.pending}
        error={create.error}
        disabled={crud.form.name.trim() === ''}
        onClose={crud.close}
        onSubmit={async () => {
          if (await create.submit()) crud.close();
        }}
      >
        <OrganizationFields form={crud.form} patch={crud.patch} />
      </FormDialog>
    </>
  );
}
